import {
	dehydrate,
	HydrationBoundary,
	QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { debounce } from 'nuqs'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7'
import { useState } from 'react'
import { RouterProvider } from 'react-aria-components'
import {
	data,
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
	type NavigateOptions,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useHref,
	useNavigate,
} from 'react-router'
import { ClientOnly } from 'remix-utils/client-only'
import { ExternalScripts } from 'remix-utils/external-scripts'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { GeneralErrorBoundary } from './components/error-boundary'
import { DOMAIN_ASSETS } from './config/domain-assets'
import { getDomainConfig, getFullDomainUrl } from './config/domains'
import { createServerCaller } from './lib/trpc-server'
import { makeQueryClient, trpc } from './lib/trpc-tanstack'
import fontStyleSheetUrl from './styles/fonts.css?url'
import globalStyleSheetUrl from './styles/global.css?url'
import { getEnv } from './utils/env.server'
import { getHoneypot } from './utils/honeypot.server'
import { combineHeaders, cn, DEFAULT_DEBOUNCE_TIME } from './utils/misc'
import { useNonce } from './utils/nonce-provider'
import { PHProvider } from './utils/posthog-provider'
import { loadSearchParams } from './utils/search-params'
import { makeTimings, time } from './utils/timing.server'

declare module 'react-aria-components' {
	interface RouterConfig {
		routerOptions: NavigateOptions
	}
}

// https://github.com/argos-ci/argos/blob/4822931b05c78e1b4a79e15cf4437fb0297369a6/apps/frontend/src/router.tsx#L21-L31
function useAbsoluteHref(path: string) {
	const relative = useHref(path)
	if (
		path.startsWith('https://') ||
		path.startsWith('http://') ||
		path.startsWith('mailto:')
	) {
		return path
	}
	return relative
}

export const links: LinksFunction = () => {
	// Base links - domain-specific assets will be added via meta tags
	return [
		{ rel: 'preload', href: fontStyleSheetUrl, as: 'style' },
		{ rel: 'stylesheet', href: fontStyleSheetUrl },
		{ rel: 'stylesheet', href: globalStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const domainConfig = data?.domainConfig

	// Get domain-specific assets
	const assets = domainConfig
		? DOMAIN_ASSETS[domainConfig.domainKey]
		: DOMAIN_ASSETS.framericons

	return [
		{
			name: 'description',
			content: domainConfig?.description || 'Premium icon sets for designers',
		},
		// Favicon links (sole source - MetaTags doesn't include these)
		{ tagName: 'link', rel: 'icon', href: assets.faviconIco, sizes: '48x48' },
		{
			tagName: 'link',
			rel: 'icon',
			type: 'image/svg+xml',
			href: assets.favicon,
		},
		{ tagName: 'link', rel: 'apple-touch-icon', href: assets.appleTouchIcon },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')

	const queryClient = makeQueryClient()
	const caller = createServerCaller(request)

	// Prefetch user data via tRPC
	const user = await time(
		async () => {
			await queryClient.prefetchQuery({
				queryKey: trpc.user.getCurrent.queryKey(),
				queryFn: () => caller.user.getCurrent(),
				staleTime: 1000 * 60 * 5, // 5 min
				gcTime: 1000 * 60 * 30, // 30 min
				retry: false,
			})
			return queryClient.getQueryData(trpc.user.getCurrent.queryKey()) as
				| Awaited<ReturnType<typeof caller.user.getCurrent>>
				| undefined
		},
		{ timings, type: 'prefetch user', desc: 'prefetch user in root' },
	)

	const honeyProps = getHoneypot().getInputProps()

	// Load search params to get dark mode preference
	const url = new URL(request.url)
	const searchParams = loadSearchParams(url.searchParams)

	// Get domain configuration
	const domainConfig = getDomainConfig(request)
	const origin = getFullDomainUrl(request)

	return data(
		{
			dehydratedState: dehydrate(queryClient),
			origin,
			ENV: getEnv(),
			honeyProps,
			darkMode: searchParams.darkMode,
			domainConfig,
		},
		{
			headers: combineHeaders({ 'Server-Timing': timings.toString() }),
		},
	)
}

// root isn't remounted again so we don't need to export clientLoader. a shouldRevalidate is enough so root loaderData is never null (might be null in error boundaries)
export function shouldRevalidate() {
	return false
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	env = {},
	darkMode,
}: {
	children: React.ReactNode
	nonce: string
	env?: Record<string, string | undefined>
	darkMode?: string
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={cn(darkMode === 'true' ? 'dark-mode' : '')}>
			<head>
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				<ExternalScripts />
			</head>
			<body className="bg-bg-primary text-text-primary min-h-screen">
				<div className="bg-bg-primary relative flex min-h-screen flex-col">
					{children}
				</div>
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()

	return (
		<Document nonce={nonce} env={data?.ENV} darkMode={data?.darkMode}>
			{children}
		</Document>
	)
}

function App() {
	const navigate = useNavigate()

	return (
		<RouterProvider navigate={navigate} useHref={useAbsoluteHref}>
			<Outlet />
		</RouterProvider>
	)
}

function AppWithProviders() {
	const loaderData = useLoaderData<typeof loader>()
	const [queryClient] = useState(makeQueryClient)

	return (
		<PHProvider>
			<QueryClientProvider client={queryClient}>
				<HydrationBoundary state={loaderData?.dehydratedState}>
					<NuqsAdapter
						defaultOptions={{
							shallow: false, // we don't want to trigger server side fetch but cannot set it to true while using debounce for some reason https://github.com/47ng/nuqs/blob/next/errors/NUQS-422.md
							scroll: false,
							clearOnDefault: true,
							limitUrlUpdates: debounce(DEFAULT_DEBOUNCE_TIME),
						}}
					>
						<HoneypotProvider {...(loaderData?.honeyProps ?? {})}>
							<App />
						</HoneypotProvider>
					</NuqsAdapter>
				</HydrationBoundary>
				<ClientOnly>{() => <ReactQueryDevtools />}</ClientOnly>
			</QueryClientProvider>
		</PHProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary

// External scripts for specific domains
export const handle = {
	scripts: (loaderData: ReturnType<typeof useLoaderData<typeof loader>>) => {
		if (loaderData?.domainConfig?.domainKey === 'notionicons') {
			return [
				{
					src: 'https://analytics.ahrefs.com/analytics.js',
					'data-key': '/3zM/TaSliQU8w/oZ+DGHg',
					async: true,
				},
			]
		}
		return []
	},
}
